import { useEffect } from "react";
import { FaUserFriends, FaExpandArrowsAlt, FaCheckCircle, FaPhoneAlt, FaWhatsapp } from "react-icons/fa";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import BookingForm from "../components/BookingForm";
import CTASection from "../components/CTASection";
import { lawnsData, eventTypes } from "../data/lawnsData";
import { siteConfig } from "../data/siteConfig";
import "../styles/Lawns.css";

const Lawns = () => {
  useEffect(() => { document.title = "Lawns & Open Venues | USHA Resort, Patna"; }, []);

  return (
    <>
      <PageHeader
        title="Our Lawns"
        subtitle="Home · Lawns"
        image="/images/Lawns/Lawn-1.png"
      />

      {/* ---------- Intro ---------- */}
      <section>
        <div className="container">
          <SectionTitle
            tag="Open-Air Venues"
            title="Celebrate Under the Open Sky"
            subtitle="Sprawling green lawns at USHA Resort set the perfect stage for weddings, receptions, parties and corporate gatherings — with nature as your backdrop."
          />

          <div className="lawns-intro gap-top">
            <div className="lawns-intro-media">
              <img src="/images/Lawns/Lawn.JPG" alt="USHA Resort Lawns" loading="lazy" />
            </div>
            <div className="lawns-intro-content">
              <h3>The Perfect Outdoor Venue in Patna</h3>
              <p>
                Our beautifully maintained lawns offer the perfect blend of open space, greenery
                and professional event support. From mandap setups to stage, lighting and catering
                arrangements — our event team handles everything so you can enjoy your celebration
                stress-free.
              </p>
              <ul>
                {[
                  "Multiple lawn options for every guest count",
                  "In-house décor & catering support",
                  "Dedicated event coordinator for every booking",
                  "Ample parking & 24×7 security",
                ].map((point) => (
                  <li key={point}><FaCheckCircle /> {point}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Lawns cards ---------- */}
      <section className="amenities-section">
        <div className="container">
          <SectionTitle
            tag="Our Venues"
            title="Lawns for Every Occasion"
            subtitle="Choose the perfect lawn based on your guest count, occasion and style."
          />

          <div className="lawns-grid gap-top">
            {lawnsData.map((lawn) => (
              <article className="lawn-card" key={lawn.id}>
                <div className="lawn-img">
                  <img src={lawn.image} alt={lawn.name} loading="lazy" />
                  <span className="lawn-capacity"><FaUserFriends /> {lawn.capacity}</span>
                </div>

                <div className="lawn-body">
                  <h3>{lawn.name}</h3>

                  <div className="lawn-meta">
                    <span><FaUserFriends /> {lawn.capacity}</span>
                    <span><FaExpandArrowsAlt /> {lawn.area}</span>
                  </div>

                  <p>{lawn.short}</p>

                  <div className="lawn-features">
                    {lawn.features.map((f) => <span key={f}>{f}</span>)}
                  </div>

                  <div className="lawn-actions">
                    <a href="#booking" className="btn btn-primary">Enquire Now</a>
                    <a
                      href={`https://wa.me/${siteConfig.whatsapp}?text=${encodeURIComponent(`Hello USHA Resort! I want to enquire about ${lawn.name}.`)}`}
                      target="_blank" rel="noreferrer" className="lawn-wa"
                    >
                      <FaWhatsapp /> WhatsApp
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Event types ---------- */}
      <section>
        <div className="container">
          <SectionTitle
            tag="We Host"
            title="Events on Our Lawns"
            subtitle="From sacred rituals to grand parties — every celebration finds its home here."
          />
          <div className="event-types gap-top">
            {eventTypes.map(({ icon: Icon, title }) => (
              <div className="event-type" key={title}>
                <Icon />
                <span>{title}</span>
              </div>
            ))}
          </div>

          <div className="lawns-cta-note">
            <p>Not sure which lawn fits your guest count? <FaPhoneAlt /> Call us at{" "}
              <a href={siteConfig.phoneHref}>{siteConfig.phone}</a> — our team will help you choose.
            </p>
          </div>
        </div>
      </section>

      <CTASection />

      {/* ---------- Booking ---------- */}
      <section className="booking-section" id="booking">
        <div className="container">
          <SectionTitle
            tag="Lawn Booking"
            title="Book Your Lawn"
            subtitle="Share your event date and guest count — our team will confirm availability on WhatsApp."
          />
          <BookingForm />
        </div>
      </section>
    </>
  );
};

export default Lawns;